import { ApplicationRef, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ExternalLinkEnhancer {
  private appRef = inject(ApplicationRef);
  private router = inject(Router);
  private observer: MutationObserver | null = null;

  init(): void {
    // Initial enhancement after app is stable
    this.appRef.isStable
      .pipe(
        filter((stable) => stable),
        first(),
      )
      .subscribe(() => {
        this.enhance();
        this.observe();
      });

    // Re-run after every navigation
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.appRef.isStable
          .pipe(
            filter((stable) => stable),
            first(),
          )
          .subscribe(() => {
            this.enhance();
            // Optionally disconnect and reconnect observer if needed
          });
      });
  }

  // Material Symbols `open_in_new` glyph (24px), matching the icon used elsewhere
  // in the app (e.g. the help dialog). Inlined because this directive works on raw
  // DOM and can't render an Angular `<mat-icon>`.
  private static readonly OPEN_IN_NEW_PATH =
    'M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z';

  private static readonly EDITOR_SELECTOR =
    '.ProseMirror, [contenteditable="true"], [contenteditable=""]';

  enhance(root: ParentNode = document): void {
    const links =
      root.querySelectorAll<HTMLAnchorElement>('a[target="_blank"]');
    for (const anchor of links) {
      if (anchor.dataset['externalLinkEnhanced']) {
        continue;
      }

      // Never touch links inside a rich-text editor (ngx-editor / ProseMirror).
      // ProseMirror owns that DOM and undoes foreign changes by re-rendering
      // the link, which the observer then sees as a new anchor to enhance, so
      // the enhancer and the editor fight over every new-tab link (#8168).
      if (anchor.closest(ExternalLinkEnhancer.EDITOR_SELECTOR)) {
        continue;
      }

      // Compute the label before injecting the icon so the icon's (empty) text
      // doesn't affect it; the icon is aria-hidden so it never reaches the SR.
      // An image-only link (e.g. a logo) has no textContent, so fall back to
      // the contained image's accessible name before the generic label.
      const label =
        (anchor.textContent ?? '').trim() || this.imageAccessibleName(anchor);
      anchor.setAttribute(
        'aria-label',
        label ? `${label} (opens in new tab)` : 'Opens in new tab',
      );

      anchor.appendChild(this.createIcon());

      const rel = new Set(
        (anchor.getAttribute('rel') ?? '').split(' ').filter(Boolean),
      );
      rel.add('noopener');
      rel.add('noreferrer');
      anchor.setAttribute('rel', [...rel].join(' '));

      anchor.dataset['externalLinkEnhanced'] = 'true';
    }
  }

  // An image-only link's accessible name comes from its image, not its
  // (empty) textContent. This is a deliberate subset of the full accessible
  // name computation, sufficient for this codebase: read the first contained
  // img's aria-label, then its alt, preferring aria-label as the more
  // specific, explicit override.
  private imageAccessibleName(anchor: HTMLAnchorElement): string {
    const img = anchor.querySelector('img');
    if (!img) {
      return '';
    }

    const ariaLabel = (img.getAttribute('aria-label') ?? '').trim();
    return ariaLabel || (img.getAttribute('alt') ?? '').trim();
  }

  private createIcon(): SVGSVGElement {
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNs, 'svg');
    svg.classList.add('external-link-icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '1em');
    svg.setAttribute('height', '1em');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.style.marginInlineStart = '0.25em';
    svg.style.verticalAlign = 'text-bottom';

    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('d', ExternalLinkEnhancer.OPEN_IN_NEW_PATH);
    svg.appendChild(path);

    return svg;
  }

  observe(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLElement) {
            this.enhance(node);
          }
        }
      }
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}
